import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import LinkIcon from "@mui/icons-material/Link";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CircleIcon from "@mui/icons-material/Circle";
import VideocamIcon from "@mui/icons-material/Videocam";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import { v4 as uuidv4 } from "uuid";
import {
  ALLOWED_VIDEO_EXTENSIONS,
  MAX_VIDEO_SIZE_BYTES,
  MAX_VIDEO_SIZE_MB,
  extractMetaFromFile,
  isValidDuration,
  isValidVideoUrl,
  detectThumbnailFromUrl,
} from "./videoMeta";
import {
  createTutorialAction,
  getModulesAction,
  updateTutorialAction,
  uploadKBAttachmentsAction,
} from "../../Actions/KnowledgeBaseAction";
import CustomSnackbar from "../Common/snackBar";
import { KB_MODULE_MENU_PROPS, KB_MODULE_SELECT_CLASS } from "./moduleDisplay";
import KBRichEditor from "./KBRichEditor";
import "../../components/Common/FormModal.css";
import "./Knowledgebase.css";

const slugify = (value = "") =>
  String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 220);

const createBlock = (type) => {
  const id = uuidv4();
  if (type === "content") return { id, type, html: "" };
  if (type === "video") {
    return {
      id,
      type,
      videoSourceType: "url",
      videoUrl: "",
      videoFile: null,
      videoFileError: "",
      thumbnail: "",
      duration: "",
    };
  }
  return { id, type: "attachment", files: [] };
};

const blocksFromTutorial = (tutorial) => {
  if (!tutorial) return [];
  const next = [];
  if (tutorial.content) {
    next.push({ id: uuidv4(), type: "content", html: tutorial.content });
  }
  const atts = Array.isArray(tutorial.attachments) ? tutorial.attachments : [];
  const videoAtt = atts.find((a) =>
    (a.mediaType || "").toLowerCase().startsWith("video"),
  );
  const otherAtts = atts.filter(
    (a) => !(a.mediaType || "").toLowerCase().startsWith("video"),
  );
  if (tutorial.videoUrl || videoAtt) {
    next.push({
      id: uuidv4(),
      type: "video",
      videoSourceType:
        tutorial.videoSourceType ||
        (videoAtt?.type === "file" ? "file" : "url"),
      videoUrl: tutorial.videoUrl || videoAtt?.url || "",
      videoFile: null,
      videoFileError: "",
      thumbnail: tutorial.thumbnail || "",
      duration: tutorial.duration || "",
    });
  }
  if (otherAtts.length) {
    next.push({ id: uuidv4(), type: "attachment", files: otherAtts });
  }
  return next;
};

const KBTextField = (props) => (
  <TextField fullWidth size="small" {...props} />
);

const KBCardSection = ({ title, subtitle, icon, onRemove, children }) => (
  <div className="kb-form-section">
    <div className="kb-form-section-head">
      <span className="kb-form-section-icon" aria-hidden>
        {icon}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <h3>{title}</h3>
        {subtitle ? <p>{subtitle}</p> : null}
      </div>
      {onRemove ? (
        <Tooltip title="Remove section" arrow>
          <IconButton size="small" onClick={onRemove} aria-label="Remove section">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
    </div>
    <div>{children}</div>
  </div>
);

const KBAttachmentIcon = ({ mediaType }) => {
  const t = (mediaType || "").toLowerCase();
  if (t.startsWith("video"))
    return <VideocamOutlinedIcon sx={{ fontSize: 16, color: "#22419e" }} />;
  if (t.startsWith("image"))
    return <ImageOutlinedIcon sx={{ fontSize: 16, color: "#22419e" }} />;
  return <DescriptionOutlinedIcon sx={{ fontSize: 16, color: "#22419e" }} />;
};

const KBAttachmentStatusIcon = ({ status }) => {
  if (status === "failed")
    return <ErrorIcon sx={{ fontSize: 14, color: "error.main" }} />;
  if (status === "completed")
    return <CheckCircleIcon sx={{ fontSize: 14, color: "success.main" }} />;
  return <HourglassEmptyIcon sx={{ fontSize: 14, color: "warning.main" }} />;
};

const KBImageThumb = ({ url, alt }) =>
  url ? (
    <img
      src={url}
      alt={alt || ""}
      onError={(e) => {
        e.currentTarget.style.display = "none";
      }}
      style={{
        width: 48,
        height: 48,
        objectFit: "cover",
        borderRadius: 8,
        border: "1px solid rgba(0,0,0,0.06)",
      }}
    />
  ) : null;

const ArticleFormDialog = ({
  open = true,
  onClose,
  tutorial,
  modules: modulesProp = [],
  fixedModuleSlug = "",
  fixedProductType = "",
  onSaved,
  asPage = true,
}) => {
  const isEdit = Boolean(tutorial && tutorial.id);
  const isOpen = true;

  const [modules, setModules] = useState(
    Array.isArray(modulesProp) ? modulesProp : [],
  );

  const [productType, setProductType] = useState(fixedProductType || "");
  const [moduleSlug, setModuleSlug] = useState(fixedModuleSlug || "");
  const [title, setTitle] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [description] = useState("");
  const [sortOrder, setSortOrder] = useState(0);
  const [slug, setSlug] = useState("");
  const [slugManual, setSlugManual] = useState(false);
  const [status, setStatus] = useState("published");
  const [visibility, setVisibility] = useState("public");
  const [blocks, setBlocks] = useState([]);

  const [productTypeError, setProductTypeError] = useState("");
  const [titleError, setTitleError] = useState("");
  const [moduleError, setModuleError] = useState("");
  const [slugError, setSlugError] = useState("");
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);

  const showSnack = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const updateBlock = (id, patch) => {
    setBlocks((prev) =>
      prev.map((b) => (b.id === id ? { ...b, ...patch } : b)),
    );
  };

  const removeBlock = (id) => {
    setBlocks((prev) => prev.filter((b) => b.id !== id));
  };

  const addBlock = (type) => {
    setBlocks((prev) => [...prev, createBlock(type)]);
    setAddMenuAnchor(null);
  };

  useEffect(() => {
    if (isOpen && modules.length === 0) {
      getModulesAction()
        .then((res) => setModules(Array.isArray(res) ? res : []))
        .catch(() => {});
    }
  }, [isOpen, modules.length]);

  useEffect(() => {
    if (!isOpen) return;
    if (!isEdit) {
      setProductType(fixedProductType || "");
      setModuleSlug(fixedModuleSlug || (modules[0] ? modules[0].slug : ""));
      setTitle("");
      setShortDescription("");
      setSortOrder(0);
      setSlug("");
      setSlugManual(false);
      setStatus("published");
      setVisibility("public");
      setBlocks([]);
      setProductTypeError("");
      setTitleError("");
      setModuleError("");
      setSlugError("");
      return;
    }

    if (tutorial?.id) {
      setProductType(tutorial.productType || "");
      setModuleSlug(tutorial.moduleSlug || fixedModuleSlug || "");
      setTitle(tutorial.title || "");
      setShortDescription(tutorial.shortDescription || "");
      setSortOrder(tutorial.sortOrder ?? 0);
      setSlug(tutorial.slug || slugify(tutorial.title || ""));
      setSlugManual(Boolean(tutorial.slug));
      setStatus(
        tutorial.status === "draft" || tutorial.status === "published"
          ? tutorial.status
          : tutorial.isActive === false
            ? "draft"
            : "published",
      );
      setVisibility(tutorial.visibility === "private" ? "private" : "public");
      setBlocks(blocksFromTutorial(tutorial));
    }
    // eslint-disable-next-line
  }, [tutorial, isOpen]);

  const handleVideoUrlChange = (blockId, value) => {
    const patch = { videoUrl: value, videoFileError: "" };
    if (value) {
      const check = isValidVideoUrl(value);
      if (!check.valid) {
        patch.videoFileError = check.message;
      } else {
        const det = detectThumbnailFromUrl(value);
        if (det) patch.thumbnail = det;
      }
    }
    updateBlock(blockId, patch);
  };

  const validateVideoFile = async (blockId, file, currentThumb, currentDuration) => {
    if (!file) {
      updateBlock(blockId, { videoFile: null, videoFileError: "" });
      return;
    }
    const ext = file.name.split(".").pop()?.toLowerCase() || "";
    if (!ALLOWED_VIDEO_EXTENSIONS.includes(ext)) {
      updateBlock(blockId, {
        videoFile: null,
        videoFileError: `Invalid file type. Allowed: ${ALLOWED_VIDEO_EXTENSIONS.map(
          (e) => "." + e.toUpperCase(),
        ).join(", ")}`,
      });
      return;
    }
    if (file.size > MAX_VIDEO_SIZE_BYTES) {
      updateBlock(blockId, {
        videoFile: null,
        videoFileError: `Video file is too large (${Math.round(
          file.size / 1024 / 1024,
        )} MB). Max: ${MAX_VIDEO_SIZE_MB} MB`,
      });
      return;
    }
    const patch = { videoFile: file, videoFileError: "" };
    try {
      const meta = await extractMetaFromFile(file);
      if (meta.thumbnailDataUrl && !currentThumb)
        patch.thumbnail = meta.thumbnailDataUrl;
      if (meta.durationLabel && !currentDuration)
        patch.duration = meta.durationLabel;
    } catch (_) {}
    updateBlock(blockId, patch);
  };

  const handleAttachmentFiles = async (blockId, files, currentFiles) => {
    if (!files || files.length === 0) return;
    const totalOthers = blocks
      .filter((b) => b.type === "attachment" && b.id !== blockId)
      .reduce((sum, b) => sum + (b.files?.length || 0), 0);
    if (totalOthers + currentFiles.length + files.length > 10) {
      showSnack(
        `You can attach up to 10 files across all attachment sections.`,
        "error",
      );
      return;
    }
    try {
      const urls = await uploadKBAttachmentsAction(files);
      const newAtts = [];
      for (let i = 0; i < files.length; i++) {
        const f = files[i];
        const url = Array.isArray(urls) ? urls[i] || urls[0] : urls;
        const mediaType = (f.type || "").toLowerCase().startsWith("image")
          ? "image"
          : "application";
        newAtts.push({
          id: uuidv4(),
          type: "file",
          mediaType,
          url,
          label: f.name,
          uploadStatus: "completed",
          size: f.size || 0,
        });
      }
      updateBlock(blockId, { files: [...currentFiles, ...newAtts] });
      showSnack(`${newAtts.length} file(s) uploaded.`);
    } catch (err) {
      console.error(err);
      showSnack(err?.message || "Failed to upload attachments", "error");
    }
  };

  const validate = () => {
    let isValid = true;
    setProductTypeError("");
    setTitleError("");
    setModuleError("");
    setSlugError("");

    if (!productType) {
      setProductTypeError("Please select a product type");
      isValid = false;
    }
    if (!title.trim()) {
      setTitleError("Title is required");
      isValid = false;
    }
    if (!moduleSlug) {
      setModuleError("Please select a module");
      isValid = false;
    }
    const nextSlug = slugify(slug || title);
    if (!nextSlug) {
      setSlugError("Slug is required");
      isValid = false;
    } else if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(nextSlug)) {
      setSlugError("Use lowercase letters, numbers, and hyphens only");
      isValid = false;
    }

    const nextBlocks = blocks.map((b) => ({ ...b }));
    let blocksChanged = false;
    nextBlocks.forEach((b) => {
      if (b.type !== "video") return;
      if (b.duration && !isValidDuration(b.duration)) {
        b.videoFileError = "Use format mm:ss or h:mm:ss";
        blocksChanged = true;
        isValid = false;
      }
      if (b.videoSourceType === "url" && b.videoUrl?.trim()) {
        const check = isValidVideoUrl(b.videoUrl.trim());
        if (!check.valid) {
          b.videoFileError = check.message;
          blocksChanged = true;
          isValid = false;
        }
      }
    });
    if (blocksChanged) setBlocks(nextBlocks);
    return isValid;
  };

  const buildPayloadFromBlocks = () => {
    const contentHtml = blocks
      .filter((b) => b.type === "content")
      .map((b) => (b.html || "").trim())
      .filter(Boolean)
      .join("\n");

    const videoBlocks = blocks.filter((b) => b.type === "video");
    const attachFiles = blocks
      .filter((b) => b.type === "attachment")
      .flatMap((b) => b.files || []);

    let primaryVideoFile = null;
    let videoSourceType = "url";
    let videoUrl = "";
    let thumbnail = "";
    let duration = "";
    const videoAttachments = [];

    videoBlocks.forEach((vb) => {
      if (vb.videoSourceType === "file" && vb.videoFile && !primaryVideoFile) {
        primaryVideoFile = vb.videoFile;
        videoSourceType = "file";
        thumbnail = vb.thumbnail || thumbnail;
        duration = vb.duration || duration;
        videoAttachments.push({
          id: uuidv4(),
          type: "file",
          mediaType: "video",
          url: null,
          label: vb.videoFile.name,
          uploadStatus: "pending",
          size: vb.videoFile.size,
        });
        return;
      }
      if (vb.videoUrl?.trim()) {
        if (!videoUrl && !primaryVideoFile) {
          videoUrl = vb.videoUrl.trim();
          videoSourceType = "url";
          thumbnail = vb.thumbnail || thumbnail;
          duration = vb.duration || duration;
        }
        videoAttachments.push({
          id: uuidv4(),
          type: "url",
          mediaType: "video",
          url: vb.videoUrl.trim(),
          label: "Video",
          uploadStatus: "completed",
        });
      }
    });

    // Avoid duplicate URL video entries when primary URL was also pushed
    const seen = new Set();
    const dedupedVideoAtts = videoAttachments.filter((a) => {
      if (!a.url) return true;
      if (seen.has(a.url)) return false;
      seen.add(a.url);
      return true;
    });

    return {
      content: contentHtml || null,
      videoSourceType,
      videoUrl: videoSourceType === "url" ? videoUrl || null : null,
      thumbnail: thumbnail || null,
      duration: duration || null,
      videoFile: primaryVideoFile,
      attachmentsJson: JSON.stringify([...attachFiles, ...dedupedVideoAtts]),
    };
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    const built = buildPayloadFromBlocks();
    const payload = {
      productType,
      moduleSlug,
      title: title.trim(),
      slug: slugify(slug || title),
      status,
      visibility,
      isActive: status === "published",
      shortDescription: shortDescription.trim() || null,
      content: built.content,
      description: description || null,
      videoSourceType: built.videoSourceType,
      videoUrl: built.videoUrl,
      thumbnail: built.thumbnail,
      duration: built.duration,
      sortOrder: Number(sortOrder) || 0,
      attachmentsJson: built.attachmentsJson,
    };
    try {
      let result;
      if (isEdit) {
        result = await updateTutorialAction(tutorial.id, payload, built.videoFile);
      } else {
        result = await createTutorialAction(payload, built.videoFile);
      }
      const createdData = result?.data || result;
      const uploading =
        createdData?.uploadStatus === "pending" ||
        createdData?.uploadStatus === "processing" ||
        (built.videoSourceType === "file" && Boolean(built.videoFile));

      if (uploading) {
        showSnack(
          isEdit
            ? "Article saved. Video is uploading in the background — check progress on the list."
            : "Article created. Video is uploading in the background — check progress on the list.",
        );
      } else {
        showSnack(isEdit ? "Knowledge Base updated" : "Knowledge Base created");
      }
      if (onSaved) onSaved({ ...createdData, _videoUploading: uploading });
      return;
    } catch (err) {
      console.error(err);
      showSnack(
        err?.response?.data?.message ||
          err?.message ||
          (isEdit
            ? "Failed to update Knowledge Base"
            : "Failed to create Knowledge Base"),
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  void asPage;
  void open;

  return (
    <div className="kb-form-page is-fullpage">
      <div className="kb-form-page-content">
        <Stack spacing={1.5}>
          <KBCardSection
            title="Module & Basic Info"
            subtitle="Choose a product type, module, and set the article's title"
            icon={<DescriptionIcon />}
          >
            <FormControl
              error={Boolean(productTypeError)}
              component="fieldset"
              sx={{ mb: 2.5 }}
            >
              <FormLabel
                component="legend"
                sx={{
                  fontWeight: 600,
                  fontSize: 13,
                  mb: 0.5,
                  color: "#1e293b",
                }}
              >
                Product Type <span className="kb-req">*</span>
              </FormLabel>
              <RadioGroup
                row
                name="productType"
                value={productType}
                onChange={(e) => {
                  setProductType(e.target.value);
                  if (e.target.value) setProductTypeError("");
                }}
              >
                {["QBot", "Tripola", "KYC", "PayCentral"].map((pType) => (
                  <FormControlLabel
                    key={pType}
                    value={pType}
                    control={<Radio size="small" />}
                    label={pType}
                    disabled={
                      isEdit ||
                      Boolean(fixedModuleSlug) ||
                      Boolean(fixedProductType)
                    }
                    sx={{ "& .MuiFormControlLabel-label": { fontSize: 14 } }}
                  />
                ))}
              </RadioGroup>
              {productTypeError && (
                <FormHelperText error>{productTypeError}</FormHelperText>
              )}
            </FormControl>

            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              sx={{ mb: 2 }}
            >
              <FormControl
                fullWidth
                error={Boolean(moduleError)}
                disabled={Boolean(fixedModuleSlug)}
              >
                <InputLabel id="kb-module-label" size="small">
                  Module <span className="kb-req">*</span>
                </InputLabel>
                <Select
                  className={KB_MODULE_SELECT_CLASS}
                  labelId="kb-module-label"
                  value={moduleSlug}
                  label="Module *"
                  size="small"
                  onChange={(e) => {
                    setModuleSlug(e.target.value);
                    setModuleError("");
                  }}
                  MenuProps={KB_MODULE_MENU_PROPS}
                >
                  {modules.map((m) => (
                    <MenuItem key={m.slug} value={m.slug}>
                      {m.moduleName}
                    </MenuItem>
                  ))}
                </Select>
                {moduleError && (
                  <FormHelperText error>{moduleError}</FormHelperText>
                )}
              </FormControl>
              <KBTextField
                label="Sort order"
                type="number"
                InputProps={{ inputProps: { min: 0 } }}
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              />
            </Stack>

            <KBTextField
              label="Title"
              required
              value={title}
              error={Boolean(titleError)}
              helperText={titleError}
              onChange={(e) => {
                const nextTitle = e.target.value;
                setTitle(nextTitle);
                if (nextTitle.trim()) setTitleError("");
                if (!slugManual) {
                  setSlug(slugify(nextTitle));
                  setSlugError("");
                }
              }}
              sx={{ mb: 2 }}
            />

            <KBTextField
              label="Slug"
              required
              value={slug}
              error={Boolean(slugError)}
              helperText={
                slugError ||
                "URL-friendly identifier (auto from title; you can edit)"
              }
              onChange={(e) => {
                setSlugManual(true);
                setSlug(slugify(e.target.value));
                setSlugError("");
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LinkIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": { background: "#f1f5f9" },
              }}
            />

            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel id="kb-status-label">Status</InputLabel>
              <Select
                labelId="kb-status-label"
                label="Status"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                renderValue={(selected) => (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircleIcon
                      sx={{
                        fontSize: 10,
                        color: selected === "published" ? "#22c55e" : "#94a3b8",
                      }}
                    />
                    {selected === "published" ? "Published" : "Draft"}
                  </Box>
                )}
              >
                <MenuItem value="published">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircleIcon sx={{ fontSize: 10, color: "#22c55e" }} />
                    Published
                  </Box>
                </MenuItem>
                <MenuItem value="draft">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircleIcon sx={{ fontSize: 10, color: "#94a3b8" }} />
                    Draft
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            <FormControl component="fieldset" sx={{ mb: 2 }}>
              <FormLabel
                component="legend"
                sx={{
                  fontWeight: 600,
                  fontSize: 13,
                  mb: 0.5,
                  color: "#1e293b",
                }}
              >
                Visibility
              </FormLabel>
              <RadioGroup
                row
                name="visibility"
                value={visibility}
                onChange={(e) => setVisibility(e.target.value)}
              >
                <FormControlLabel
                  value="public"
                  control={<Radio size="small" />}
                  label="Public"
                  sx={{ "& .MuiFormControlLabel-label": { fontSize: 14 } }}
                />
                <FormControlLabel
                  value="private"
                  control={<Radio size="small" />}
                  label={
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      Private
                      <Tooltip
                        title="Private articles stay hidden from public / tenant-facing knowledge base views."
                        arrow
                      >
                        <InfoOutlinedIcon sx={{ fontSize: 16, color: "#22c55e" }} />
                      </Tooltip>
                    </Box>
                  }
                  sx={{ "& .MuiFormControlLabel-label": { fontSize: 14 } }}
                />
              </RadioGroup>
            </FormControl>

            <KBTextField
              label="Short Description"
              placeholder="Brief summary shown below the title (max 500 chars)"
              multiline
              rows={2}
              value={shortDescription}
              onChange={(e) =>
                setShortDescription(e.target.value.slice(0, 500))
              }
              helperText={`${shortDescription.length}/500`}
            />
          </KBCardSection>

          {blocks.map((block) => {
            if (block.type === "content") {
              return (
                <KBCardSection
                  key={block.id}
                  title={`Content ${blocks.filter((b) => b.type === "content").indexOf(block) + 1}`}
                  subtitle="Write rich article content with the Knowledge Base editor"
                  icon={<DescriptionIcon />}
                  onRemove={() => removeBlock(block.id)}
                >
                  <KBRichEditor
                    value={block.html}
                    onChange={(html) => updateBlock(block.id, { html })}
                    placeholder="Start writing your article content…"
                  />
                </KBCardSection>
              );
            }

            if (block.type === "video") {
              const videoIndex =
                blocks.filter((b) => b.type === "video").indexOf(block) + 1;
              return (
                <KBCardSection
                  key={block.id}
                  title={`Video ${videoIndex}`}
                  subtitle="YouTube / Vimeo URL or upload a video file"
                  icon={<VideocamIcon />}
                  onRemove={() => removeBlock(block.id)}
                >
                  <Stack spacing={2}>
                    <ToggleButtonGroup
                      value={block.videoSourceType}
                      exclusive
                      onChange={(_, v) => {
                        if (!v) return;
                        updateBlock(block.id, {
                          videoSourceType: v,
                          videoFileError: "",
                          ...(v === "url"
                            ? { videoFile: null }
                            : {}),
                        });
                      }}
                      size="small"
                      color="primary"
                    >
                      <ToggleButton className="kb-toggle" value="url">
                        <LinkIcon sx={{ fontSize: 16, mr: 0.5 }} /> URL
                      </ToggleButton>
                      <ToggleButton className="kb-toggle" value="file">
                        <CloudUploadIcon sx={{ fontSize: 16, mr: 0.5 }} /> File
                      </ToggleButton>
                    </ToggleButtonGroup>

                    {block.videoSourceType === "url" ? (
                      <KBTextField
                        label="YouTube / Vimeo / Direct video URL"
                        placeholder="https://www.youtube.com/watch?v=..."
                        value={block.videoUrl}
                        onChange={(e) =>
                          handleVideoUrlChange(block.id, e.target.value)
                        }
                        error={Boolean(block.videoFileError)}
                        helperText={
                          block.videoFileError ||
                          "Paste a YouTube/Vimeo link, or a direct MP4/WebM link."
                        }
                      />
                    ) : (
                      <VideoFileDropzone
                        block={block}
                        onPick={(file) =>
                          validateVideoFile(
                            block.id,
                            file,
                            block.thumbnail,
                            block.duration,
                          )
                        }
                      />
                    )}

                    {block.thumbnail ? (
                      <div className="kb-thumb-preview">
                        <img
                          src={block.thumbnail}
                          alt="Thumbnail preview"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                      </div>
                    ) : null}
                  </Stack>
                </KBCardSection>
              );
            }

            if (block.type === "attachment") {
              const attachIndex =
                blocks.filter((b) => b.type === "attachment").indexOf(block) +
                1;
              return (
                <KBCardSection
                  key={block.id}
                  title={`Attachments ${attachIndex}`}
                  subtitle={`Upload images or documents for this section (${block.files.length} file(s))`}
                  icon={<AttachFileIcon />}
                  onRemove={() => removeBlock(block.id)}
                >
                  <AttachmentBlockBody
                    block={block}
                    onUpload={(files) =>
                      handleAttachmentFiles(block.id, files, block.files)
                    }
                    onRemoveFile={(fileId) =>
                      updateBlock(block.id, {
                        files: block.files.filter((f) => f.id !== fileId),
                      })
                    }
                  />
                </KBCardSection>
              );
            }

            return null;
          })}

          <div className="kb-add-more">
            {!blocks.length ? (
              <Button
                type="button"
                className="kb-add-more-btn is-primary"
                startIcon={<AddIcon />}
                onClick={() => addBlock("content")}
              >
                Add content
              </Button>
            ) : (
              <Button
                type="button"
                className="kb-add-more-btn"
                startIcon={<AddIcon />}
                onClick={(e) => setAddMenuAnchor(e.currentTarget)}
              >
                Add more
              </Button>
            )}
            <Menu
              anchorEl={addMenuAnchor}
              open={Boolean(addMenuAnchor)}
              onClose={() => setAddMenuAnchor(null)}
              anchorOrigin={{ vertical: "top", horizontal: "left" }}
              transformOrigin={{ vertical: "bottom", horizontal: "left" }}
              PaperProps={{ className: "kb-add-more-menu" }}
            >
              <MenuItem onClick={() => addBlock("content")}>
                <DescriptionIcon sx={{ fontSize: 18, mr: 1, color: "#22419e" }} />
                Content
              </MenuItem>
              <MenuItem onClick={() => addBlock("video")}>
                <VideocamIcon sx={{ fontSize: 18, mr: 1, color: "#22419e" }} />
                Video
              </MenuItem>
              <MenuItem onClick={() => addBlock("attachment")}>
                <AttachFileIcon sx={{ fontSize: 18, mr: 1, color: "#22419e" }} />
                Attachment
              </MenuItem>
            </Menu>
            {!blocks.length ? (
              <p className="kb-add-more-hint">
                Click Add content to open the editor. Use Add more for Video or Attachments.
              </p>
            ) : null}
          </div>
        </Stack>
      </div>

      <div className="kb-form-footer is-page">
        <span />
        <div className="kb-form-actions">
          <button
            type="button"
            className="fm-btn"
            onClick={() => (saving ? null : onClose?.())}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="fm-btn is-primary"
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? (
              <>
                <span
                  className="kb-spin"
                  style={{
                    width: 14,
                    height: 14,
                    display: "inline-block",
                    border: "2px solid rgba(255,255,255,0.35)",
                    borderTopColor: "#fff",
                    borderRadius: "50%",
                  }}
                />
                Saving…
              </>
            ) : isEdit ? (
              "Update"
            ) : (
              "Create"
            )}
          </button>
        </div>
      </div>

      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
      />
    </div>
  );
};

const VideoFileDropzone = ({ block, onPick }) => {
  const inputRef = useRef(null);
  return (
    <Box
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        const f = e.dataTransfer?.files?.[0];
        if (f) onPick(f);
      }}
      className={`kb-dropzone${
        block.videoFileError ? " error" : block.videoFile ? " success" : ""
      }`}
      onClick={() => inputRef.current?.click()}
    >
      <input
        ref={inputRef}
        type="file"
        accept={ALLOWED_VIDEO_EXTENSIONS.map((e) => "." + e).join(",")}
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onPick(f);
          e.target.value = "";
        }}
      />
      <VideocamIcon sx={{ fontSize: 36 }} />
      <span className="kb-dropzone-title">
        {block.videoFile
          ? block.videoFile.name
          : "Drag video here or click to select"}
      </span>
      <span className="kb-dropzone-hint">
        {block.videoFile
          ? `Size: ${(block.videoFile.size / 1024 / 1024).toFixed(2)} MB`
          : `Up to ${MAX_VIDEO_SIZE_MB} MB · ${ALLOWED_VIDEO_EXTENSIONS.map(
              (e) => "." + e.toUpperCase(),
            ).join(", ")}`}
      </span>
      {block.videoFileError ? (
        <Typography
          variant="body2"
          color="error"
          sx={{ mt: 1, fontWeight: 600 }}
        >
          {block.videoFileError}
        </Typography>
      ) : null}
    </Box>
  );
};

const AttachmentBlockBody = ({ block, onUpload, onRemoveFile }) => {
  const inputRef = useRef(null);
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx,.txt"
        hidden
        onChange={(e) => {
          onUpload(Array.from(e.target.files || []));
          e.target.value = "";
        }}
      />
      <Box
        className="kb-dropzone"
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onUpload(Array.from(e.dataTransfer?.files || []));
        }}
        onClick={() => inputRef.current?.click()}
      >
        <AttachFileIcon sx={{ fontSize: 28 }} />
        <span className="kb-dropzone-title">
          Drag files here or click to upload
        </span>
        <span className="kb-dropzone-hint">
          Max 10 files total · JPG / PNG / PDF / DOC / XLS and more
        </span>
      </Box>
      <Stack spacing={1.2} sx={{ mt: 2 }}>
        {(block.files || []).map((a) => (
          <AttachmentChipRow
            key={a.id}
            attachment={a}
            onRemove={() => onRemoveFile(a.id)}
            withThumb={(a.mediaType || "").toLowerCase().startsWith("image")}
          />
        ))}
      </Stack>
    </>
  );
};

const AttachmentChipRow = ({ attachment, onRemove, withThumb = false }) => {
  const t = (attachment.mediaType || "").toLowerCase();
  let label = attachment.label || "Attachment";
  const isVideo = t.startsWith("video");
  const isImage = t.startsWith("image");

  if (isVideo) label = attachment.label || "Video";
  else if (isImage) label = attachment.label || "Image";
  else label = attachment.label || "Document";

  const iconClass = isVideo
    ? "is-video"
    : isImage
      ? "is-image"
      : /\.pdf$/i.test(label)
        ? "is-pdf"
        : "";

  return (
    <div className="kb-doc-row">
      {withThumb && isImage ? (
        <KBImageThumb url={attachment.url} alt={label} />
      ) : (
        <span className={`kb-attach-icon ${iconClass}`}>
          {isVideo ? (
            <VideocamOutlinedIcon sx={{ fontSize: 22 }} />
          ) : isImage ? (
            <ImageOutlinedIcon sx={{ fontSize: 22 }} />
          ) : /\.pdf$/i.test(label) ? (
            <PictureAsPdfIcon sx={{ fontSize: 22 }} />
          ) : (
            <DescriptionOutlinedIcon sx={{ fontSize: 22 }} />
          )}
        </span>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 700,
            color: "#0f172a",
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            minWidth: 0,
          }}
        >
          <KBAttachmentIcon mediaType={attachment.mediaType} />
          <Box
            component="a"
            href={attachment.url || "#"}
            target={attachment.url ? "_blank" : undefined}
            rel="noopener noreferrer"
            sx={{
              color: attachment.url ? "#22419e" : "#0f172a",
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {label}
          </Box>
          {attachment.uploadStatus &&
            attachment.uploadStatus !== "completed" && (
              <KBAttachmentStatusIcon status={attachment.uploadStatus} />
            )}
        </Typography>
        <Typography variant="caption" sx={{ color: "#64748b" }}>
          {attachment.size
            ? (attachment.size / 1024).toFixed(1) >= 1024
              ? `${(attachment.size / 1024 / 1024).toFixed(2)} MB`
              : `${(attachment.size / 1024).toFixed(1)} KB`
            : attachment.type === "url"
              ? "Linked from URL"
              : attachment.type || "File"}
        </Typography>
      </Box>
      <Tooltip title="Remove" placement="top" arrow>
        <IconButton
          onClick={onRemove}
          size="small"
          sx={{ "&:hover": { color: "#ef4444" } }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
    </div>
  );
};

export default ArticleFormDialog;
